/**
 * 現在位置の取得(CLAUDE.md §8-3)
 *
 * 圏外でもGPSは動く。ただし島の谷あいや歌碑の裏手では測位に時間がかかり、
 * 誤差も大きく出る。ここでの方針は「待ちすぎない、失敗しても止めない」。
 * 位置が取れなくてもスタンプ取得は妨げず、フラグを立てて受付判断に回す。
 */

export type LocateFailure = "denied" | "unavailable" | "timeout" | "unsupported";

export type LocateResult =
  | { ok: true; lat: number; lng: number; acc: number }
  | { ok: false; reason: LocateFailure };

/**
 * 測位の待ち時間の上限。
 * これを超えたらスタンプは gps_unverified として確定させる。
 * 屋外の初回測位は数秒〜十数秒かかることがあるが、参加者を歌碑の前で
 * 立ち止まらせ続けるほうが害が大きい。
 */
const TIMEOUT_MS = 12_000;

/**
 * 直前の測位結果を再利用してよい時間。
 * 徒歩なら30秒で動ける距離はたかが知れており、100m級のジオフェンス判定には
 * 十分。初回測位のコールドスタートを避けられる効果のほうが大きい。
 */
const MAX_AGE_MS = 30_000;

export function locate(): Promise<LocateResult> {
  if (!navigator.geolocation) {
    return Promise.resolve({ ok: false, reason: "unsupported" });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (r: LocateResult): void => {
      if (settled) return;
      settled = true;
      resolve(r);
    };

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        finish({
          ok: true,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        }),
      (err) => {
        const reason: LocateFailure =
          err.code === err.PERMISSION_DENIED
            ? "denied"
            : err.code === err.TIMEOUT
              ? "timeout"
              : "unavailable";
        finish({ ok: false, reason });
      },
      { enableHighAccuracy: true, timeout: TIMEOUT_MS, maximumAge: MAX_AGE_MS },
    );

    // getCurrentPosition の timeout が働かない端末があるため、外側でも切る
    setTimeout(() => finish({ ok: false, reason: "timeout" }), TIMEOUT_MS + 1000);
  });
}

/** 参加者向けの説明。原因ごとに「次にどうすればいいか」を示す。 */
export const LOCATE_MESSAGE: Record<LocateFailure, string> = {
  denied:
    "位置情報の使用が許可されていないため、現地確認ができませんでした。スタンプは記録されています。受付で追加の確認をお願いする場合があります。",
  unavailable:
    "位置情報を取得できませんでした。スタンプは記録されています。受付で追加の確認をお願いする場合があります。",
  timeout:
    "位置情報の取得に時間がかかっています。スタンプは記録されています。受付で追加の確認をお願いする場合があります。",
  unsupported:
    "この端末では位置情報を使えません。スタンプは記録されています。受付で追加の確認をお願いする場合があります。",
};
